import React, { useState, useEffect } from 'react';
import { Button, App } from 'antd';
import { HeartOutlined, HeartFilled } from '@ant-design/icons';
import { useAppSelector } from '../hooks/redux';
import { apiClient } from '../services/api';
import { useTranslation } from 'react-i18next';

interface FavoriteButtonProps {
  brandId: number;
  size?: 'small' | 'middle' | 'large';
  type?: 'default' | 'primary' | 'text' | 'link';
  initialFavoriteStatus?: boolean;
}

const FavoriteButton: React.FC<FavoriteButtonProps> = ({
  brandId,
  size = 'middle',
  type = 'default',
  initialFavoriteStatus = false,
}) => {
  const { isAuthenticated } = useAppSelector(state => state.auth);
  const [isFavorite, setIsFavorite] = useState(initialFavoriteStatus);
  const [loading, setLoading] = useState(false);
  const { message } = App.useApp();
  const { t } = useTranslation();

  useEffect(() => {
    setIsFavorite(initialFavoriteStatus);
  }, [initialFavoriteStatus]);

  const handleToggleFavorite = async (e: React.MouseEvent) => {
    e.stopPropagation(); // 阻止事件冒泡

    if (!isAuthenticated) {
      message.info('请先登录后再收藏品牌');
      return;
    }
    setLoading(true);

    try {
      if (isFavorite) {
        const response = await apiClient.removeFavorite(brandId);
        setIsFavorite(false);
        message.success(t('brand_card.unfavorite_success'));
      } else {
        const response = await apiClient.addFavorite(brandId);
        setIsFavorite(true);
        message.success(t('brand_card.favorite_success'));
      }
    } catch (error: unknown) {
      const errMsg =
        typeof error === 'object' && error && 'message' in error
          ? (error as { message?: string }).message
          : undefined;
      message.error(`${t('brand_card.operation_failed')}: ${errMsg || '请重试'}`);
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <Button
        type={type}
        size={size}
        icon={<HeartOutlined />}
        onClick={e => {
          e.stopPropagation();
          message.info(t('brand_card.login_to_favorite'));
        }}
      >
        {t('brand_card.favorite')}
      </Button>
    );
  }

  return (
    <Button
      type={type}
      size={size}
      loading={loading}
      icon={
        isFavorite ? (
          <HeartFilled style={{ color: '#ff4d4f' }} />
        ) : (
          <HeartOutlined />
        )
      }
      onClick={handleToggleFavorite}
      style={{
        color: isFavorite ? '#ff4d4f' : undefined,
        borderColor: isFavorite ? '#ff4d4f' : undefined,
      }}
    >
      {isFavorite ? t('brand_card.favorited') : t('brand_card.favorite')}
    </Button>
  );
};

export default FavoriteButton;
